<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faSave } from '@fortawesome/free-solid-svg-icons'
    import dayjs from 'dayjs'
    import { BillEntity } from '../../../modules/financialApi/bills/bill'
    import Zframe from '../../_common/z/zframe.svelte'
    import AccountSelect from '../../financial/accounts/account-select.svelte'
    import CategorySelect from '../../financial/categories/category-select.svelte'
    import { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    import type { PayBillDto } from '../../../modules/financialApi/bills/dto/pay-bill.dto'
    const dispatch = createEventDispatcher()

    export let billId: string = null

    let show = false
    let entity: BillEntity = new BillEntity()
    let paidAt = dayjs(new Date()).format('YYYY-MM-DD')
    let headline = ''
    let exportToAccount = false

    let accrual: string = ''
    let accrualPeriod: 'monthly' | 'yearly' = 'monthly'

    onMount(async () => {
        await loadEntity(billId)
    })

    async function loadEntity(id: string) {
        try {
            show = true
            entity = new BillEntity()
            entity = await window.api.financials.bills.getOne(id)
            entity.person = await window.api.events.persons.getOne(entity.personId)
            entity.paidValue = entity.paidValue || entity.value
            headline = entity.description
            if (entity.installmentTotal > 1) {
                headline = '(' + entity.installmentNumber + '/' + entity.installmentTotal + ') ' + entity.description
            }
            console.log('bill', entity)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        if (window.configs.financialShowAccrual) {
            if (accrual) {
                console.log('accrual', accrual)
                if (accrualPeriod === 'monthly') {
                    accrual = accrual.toString()
                    entity.accrualYear = parseInt(accrual.split('-')[0])
                    entity.accrualMonth = parseInt(accrual.split('-')[1])
                } else {
                    entity.accrualYear = parseInt(accrual)
                    entity.accrualMonth = null
                }
            }
        }
        try {
            entity.paidAt = window.api.utils.dates.setDateStringToLocalMidnight(paidAt)
            if (!exportToAccount || showIntegrationAlert) {
                entity.account = null
                entity.accountId = null
                entity.category = null
                entity.categoryId = null
                entity.accrualMonth = null
                entity.accrualYear = null
            }
            let dto: PayBillDto = {
                paidAt: entity.paidAt,
                paidValue: entity.paidValue,
                accountId: entity.account?.accountId || entity.accountId,
                categoryId: entity.category?.categoryId || entity.categoryId,
                accrualMonth: entity.accrualMonth,
                accrualYear: entity.accrualYear,
            }
            if (!validateDto(dto)) return false
            await window.api.financials.bills.pay(billId, dto)
            dispatch('change')
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto) {
        if (!dto.paidAt) {
            evaluateError('Informe a data do pagamento')
            return false
        }
        if (!dto.paidValue) {
            evaluateError('Informe o valor pago')
            return false
        }
        if (exportToAccount && !dto.accountId) {
            evaluateError('Informe a conta financeira')
            return false
        }
        if (exportToAccount && !dto.categoryId) {
            evaluateError('Informe a categoria')
            return false
        }

        return true
    }

    function evaluateError(error) {
        window.error(error)
    }

    let showIntegrationAlert = false
    function onAccountSelectChange(ev) {
        console.log('res', ev.detail)
        if (ev.detail?.source && ev.detail?.source !== 'manual') {
            showIntegrationAlert = true
        } else {
            showIntegrationAlert = false
        }
    }
</script>

<div class="root">
    <div class="grid-act">
        <div class="spacer" />
        {#if window.store.token.permissions.financial.entries.write}
            <button class="btn btn-primary" on:click={() => saveEntity()}>
                <Fa icon={faSave} /> Baixar
            </button>
        {/if}
    </div>
    <Zframe title="Baixa de conta" {headline} class="m-3">
        <div class="grid-form">
            <div class="row">
                <div class="col-md-6">
                    <div class="input-group">
                        <label for="" class="input-group-text">Valor original</label>
                        <input disabled={true} type="number" bind:value={entity.value} class="form-control" />
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <div class="input-group">
                        {#if entity.creditDebit === EntryTypeEnum.debit}
                            <label for="" class="input-group-text">Valor pago</label>
                        {:else}
                            <label for="" class="input-group-text">Valor recebido</label>
                        {/if}
                        <input type="number" bind:value={entity.paidValue} class="form-control" />
                    </div>
                </div>
                <div class="col">
                    <div class="input-group">
                        {#if entity.creditDebit === EntryTypeEnum.debit}
                            <label for="" class="input-group-text">Data do pagamento</label>
                        {:else}
                            <label for="" class="input-group-text">Data do recebimento</label>
                        {/if}

                        <input type="date" bind:value={paidAt} class="form-control" />
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <div class="form-check form-switch">
                        <input class="form-check-input" bind:checked={exportToAccount} type="checkbox" role="switch" id="installments" />
                        <label class="form-check-label" for="installments">Lançar valor em uma conta financeira</label>
                    </div>
                </div>
            </div>
        </div>
    </Zframe>
    {#if exportToAccount}
        <Zframe title="Lançar em conta" class="m-3 mt-0">
            <div class="grid-form">
                <div class="row">
                    <div class="col">
                        <AccountSelect label="Conta" bind:account={entity.account} on:change={onAccountSelectChange} />
                    </div>
                    <div class="col">
                        <CategorySelect creditDebit={entity.creditDebit} label="Categoria" bind:category={entity.category} />
                    </div>
                </div>
                {#if showIntegrationAlert}
                    <div class="row">
                        <div class="col">
                            <div class="alert alert-secondary text-center">
                                A conta selecionada é alimentada por importação e não recebe lançamentos manuais.<br />
                                O valor pago será lançado através da importação.
                            </div>
                        </div>
                    </div>
                {:else if window.configs.financialShowAccrual}
                    <div class="row">
                        <div class="col-md-6">
                            <div class="input-group">
                                <label for="" class="input-group-text">Tipo de competência</label>
                                <select bind:value={accrualPeriod} class="form-select">
                                    <option value="monthly">Mensal</option>
                                    <option value="yearly">Anual</option>
                                </select>
                            </div>
                        </div>
                        <div class="col">
                            {#if accrualPeriod === 'monthly'}
                                <div class="input-group">
                                    <label for="" class="input-group-text">Mês de competência</label>
                                    <input type="month" bind:value={accrual} class="form-control" />
                                </div>
                            {:else}
                                <div class="input-group">
                                    <label for="" class="input-group-text">Ano de competência</label>
                                    <input type="number" bind:value={accrual} class="form-control" />
                                </div>
                            {/if}
                        </div>
                    </div>
                {/if}
            </div>
        </Zframe>
    {/if}
</div>
